import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import { ToastProvider } from './components/ToastProvider';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import reportWebVitals from './reportWebVitals';
import { AuthProvider } from './contexts/AuthContext';
import { HydrationProvider } from './contexts/HydrationContext';

const PageFallback = () => <div className="min-h-[50vh] flex items-center justify-center text-gray-500">Loading...</div>;

// Auth pages
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const PasswordResetPage = lazy(() => import('./pages/PasswordResetPage'));

// Content pages
const ContactPage = lazy(() => import('./pages/ContactPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const StartProjectPage = lazy(() => import('./pages/StartProjectPage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const CookiesPage = lazy(() => import('./pages/CookiesPage'));
const SitemapPage = lazy(() => import('./pages/SitemapPage'));
const PreviewPage = lazy(() => import('./pages/PreviewPage'));

// Feature pages
const CheckoutSuccessPage = lazy(() => import(/* webpackChunkName: "checkout-success" */ './pages/CheckoutSuccessPage'));
const CartPage = lazy(() => import(/* webpackChunkName: "cart" */ './pages/CartPage'));
const CatalogPage = lazy(() => import(/* webpackChunkName: "catalog" */ './pages/CatalogPage'));
const DashboardPage = lazy(() => import(/* webpackChunkName: "dashboard" */ './pages/DashboardPage'));
const CheckoutPage = lazy(() => import(/* webpackChunkName: "checkout" */ './pages/CheckoutPage'));
const TestimonialsPage = lazy(() => import(/* webpackChunkName: "testimonials" */ './pages/TestimonialsPage'));

// Admin
const AdminLayout = lazy(() => import(/* webpackChunkName: "admin-layout" */ './components/AdminLayout'));
const AdminLoginPage = lazy(() => import(/* webpackChunkName: "admin-login" */ './pages/AdminLoginPage'));
const AdminDashboardPage = lazy(() => import(/* webpackChunkName: "admin-dashboard" */ './pages/AdminDashboardPage'));
const AdminTestimonialsPage = lazy(() => import(/* webpackChunkName: "admin-testimonials" */ './pages/AdminTestimonialsPage'));
const AdminServicesPage = lazy(() => import(/* webpackChunkName: "admin-services" */ './pages/AdminServicesPage'));
const AdminBankTransfersPage = lazy(() => import(/* webpackChunkName: "admin-bank-transfers" */ './pages/AdminBankTransfersPage'));
const AdminCouponsPage = lazy(() => import(/* webpackChunkName: "admin-coupons" */ './pages/AdminCouponsPage'));
const AdminAnalyticsPage = lazy(() => import(/* webpackChunkName: "admin-analytics" */ './pages/AdminAnalyticsPage'));

const AdminRoute = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<PageFallback />}>
    <AdminLayout>
      <Suspense fallback={<PageFallback />}>{children}</Suspense>
    </AdminLayout>
  </Suspense>
);

const renderTree = (
  <React.StrictMode>
    <HelmetProvider>
      <ToastProvider>
        <HydrationProvider>
          <AuthProvider>
            <BrowserRouter>
              <Routes>

                <Route path="/checkout/success" element={<Suspense fallback={<PageFallback />}><CheckoutSuccessPage /></Suspense>} />
                <Route path="/*" element={<App />} />
                <Route path="/catalog" element={<Layout><Suspense fallback={<PageFallback />}><CatalogPage /></Suspense></Layout>} />
                <Route path="/cart" element={<Layout><Suspense fallback={<PageFallback />}><CartPage /></Suspense></Layout>} />
                <Route path="/login" element={<Layout><Suspense fallback={<PageFallback />}><LoginPage /></Suspense></Layout>} />
                <Route path="/register" element={<Layout><Suspense fallback={<PageFallback />}><RegisterPage /></Suspense></Layout>} />
                <Route path="/forgot-password" element={<Layout><Suspense fallback={<PageFallback />}><ForgotPasswordPage /></Suspense></Layout>} />
                <Route path="/password-reset/:token" element={<Layout><Suspense fallback={<PageFallback />}><PasswordResetPage /></Suspense></Layout>} />
                <Route path="/verify-email" element={<Layout><Suspense fallback={<PageFallback />}><VerifyEmailPage /></Suspense></Layout>} />
                <Route path="/dashboard" element={<Layout><ProtectedRoute><Suspense fallback={<PageFallback />}><DashboardPage /></Suspense></ProtectedRoute></Layout>} />
                <Route path="/checkout" element={<Layout><ProtectedRoute><Suspense fallback={<PageFallback />}><CheckoutPage /></Suspense></ProtectedRoute></Layout>} />
                <Route path="/contact" element={<Layout><Suspense fallback={<PageFallback />}><ContactPage /></Suspense></Layout>} />
                <Route path="/about" element={<Layout><Suspense fallback={<PageFallback />}><AboutPage /></Suspense></Layout>} />
                <Route path="/start-project" element={<Layout><Suspense fallback={<PageFallback />}><StartProjectPage /></Suspense></Layout>} />
                <Route path="/testimonials" element={<Layout><ProtectedRoute><Suspense fallback={<PageFallback />}><TestimonialsPage /></Suspense></ProtectedRoute></Layout>} />
                <Route path="/admin/login" element={<Suspense fallback={<PageFallback />}><AdminLoginPage /></Suspense>} />
                <Route path="/admin" element={<AdminRoute><AdminDashboardPage /></AdminRoute>} />
                <Route path="/admin/testimonials" element={<AdminRoute><AdminTestimonialsPage /></AdminRoute>} />
                <Route path="/admin/services" element={<AdminRoute><AdminServicesPage /></AdminRoute>} />
                <Route path="/admin/bank-transfers" element={<AdminRoute><AdminBankTransfersPage /></AdminRoute>} />
                <Route path="/admin/coupons" element={<AdminRoute><AdminCouponsPage /></AdminRoute>} />
                <Route path="/admin/analytics" element={<AdminRoute><AdminAnalyticsPage /></AdminRoute>} />
                <Route path="/faq" element={<Layout><Suspense fallback={<PageFallback />}><FAQPage /></Suspense></Layout>} />
                <Route path="/privacy" element={<Layout><Suspense fallback={<PageFallback />}><PrivacyPage /></Suspense></Layout>} />
                <Route path="/terms" element={<Layout><Suspense fallback={<PageFallback />}><TermsPage /></Suspense></Layout>} />
                <Route path="/cookies" element={<Layout><Suspense fallback={<PageFallback />}><CookiesPage /></Suspense></Layout>} />
                <Route path="/sitemap" element={<Layout><Suspense fallback={<PageFallback />}><SitemapPage /></Suspense></Layout>} />
                <Route path="/preview/:slug" element={<Suspense fallback={<PageFallback />}><PreviewPage /></Suspense>} />

              </Routes>
            </BrowserRouter>
          </AuthProvider>
        </HydrationProvider>
      </ToastProvider>
    </HelmetProvider>
  </React.StrictMode>
);

const rootElement = document.getElementById('root') as HTMLElement;

if (rootElement.hasChildNodes()) {
  ReactDOM.hydrateRoot(rootElement, renderTree);
} else {
  ReactDOM.createRoot(rootElement).render(renderTree);
}

reportWebVitals();
